import { useState, useEffect } from "react";
import { Truck, Phone, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL_LINK, WHATSAPP_LINK } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openQuote, requestEstimate } = useDialogs();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md ${
        scrolled ? "border-b border-gray-200" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#accueil" className="flex items-center gap-2 shrink-0" data-testid="logo-home-link">
          <span className="bg-navy text-white rounded-xl p-2 flex items-center justify-center">
            <Truck className="w-5 h-5 md:w-6 md:h-6" />
          </span>
          <span className="font-heading font-extrabold text-lg md:text-xl text-navy leading-none tracking-tight">
            Express Delivery
          </span>
        </a>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-6 px-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-semibold text-navy/75 hover:text-orange transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <a
            href={PHONE_TEL_LINK}
            data-testid="header-call-button"
            className="flex items-center gap-2 text-navy font-semibold text-sm border-2 border-navy/15 rounded-full px-4 py-2 hover:border-navy transition-colors"
          >
            <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
          </a>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="flex items-center gap-2 bg-whatsapp text-white rounded-full px-4 py-2 text-sm font-semibold hover:brightness-95 transition-all hover:-translate-y-0.5"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <Button
            data-testid="header-quote-button"
            onClick={() => openQuote(null)}
            className="bg-orange hover:bg-orange-hover text-white rounded-full px-6 font-semibold transition-transform hover:-translate-y-0.5 shadow-lg shadow-orange/30"
          >
            Obtenir un devis
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-navy p-2"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-white border-t border-navy/10 px-6 py-5 flex flex-col gap-4 animate-fade-up"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-navy font-semibold text-base"
            >
              {link.label}
            </a>
          ))}
          <Button
            data-testid="mobile-menu-estimate-button"
            onClick={() => {
              setMobileOpen(false);
              requestEstimate({ source: "menu-mobile" });
            }}
            className="bg-orange hover:bg-orange-hover text-white rounded-full font-semibold mt-2"
          >
            Obtenir mon estimation
          </Button>
        </div>
      )}
    </header>
  );
}
